import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  attemptStatusLabel,
  type PaymentAttemptDTO,
  type PaymentAttemptStatus,
  type TenantPaymentAttemptDTO,
} from '@propmgt/shared';
import { api, errorMessage } from '../../api/client';
import { formatCents } from '../../lib/money';
import { formatDate, formatDateTime } from '../../lib/date';
import { ErrorState, Loading, StatusMessage } from '../ui/Feedback';

/**
 * 0.26.0 — payment ACTIVITY views (every step of an online ACH payment). Status is always shown as
 * TEXT (never color alone), and nothing reads "paid" before Stripe reports `succeeded`.
 */

function badgeClass(s: PaymentAttemptStatus): string {
  if (s === 'succeeded') return 'badge badge--ok';
  if (s === 'payment_failed' || s === 'reversed') return 'badge badge--err';
  if (s === 'processing' || s === 'requires_action') return 'badge badge--warn';
  return 'badge';
}

export function AttemptStatusBadge({ status, label }: { status: PaymentAttemptStatus; label?: string }) {
  return <span className={badgeClass(status)}>{label ?? attemptStatusLabel(status)}</span>;
}

/** Tenant-facing badge text: a succeeded payment not yet on the ledger is "posting", never "Paid". */
function tenantBadgeLabel(a: TenantPaymentAttemptDTO): string | undefined {
  if (a.status === 'succeeded' && !a.postedToLedger) return 'Cleared — posting to your account';
  return undefined;
}

const EVENT_LABELS: Record<string, string> = {
  'portal.create': 'Tenant opened the pay page',
  'portal.reopen': 'Tenant reopened the pay page (same payment)',
  'portal.resume': 'Tenant continued this payment',
  'payment_intent.created': 'Stripe: payment created',
  'payment_intent.requires_action': 'Stripe: waiting on the tenant (verification)',
  'payment_intent.processing': 'Stripe: bank debit submitted (processing)',
  'payment_intent.succeeded': 'Stripe: payment cleared',
  'payment_intent.payment_failed': 'Stripe: payment failed',
  'payment_intent.canceled': 'Stripe: payment canceled',
  'payment_intent.partially_funded': 'Stripe: partially funded',
  'charge.refunded': 'Stripe: refunded',
  'charge.dispute.created': 'Stripe: dispute opened',
  'charge.dispute.funds_withdrawn': 'Stripe: dispute funds withdrawn',
  'owner.cancel': 'Owner canceled it',
  'tenant.cancel': 'Tenant canceled it',
  'sweep.cancel': 'Auto-canceled (abandoned over 7 days)',
  'sync.upsert': 'Found by a Stripe sync',
  'sync.read': 'Updated from Stripe',
};

function eventLabel(type: string): string {
  if (type.startsWith('client.confirm:')) return `Tenant's browser reported: ${type.slice('client.confirm:'.length).replace(/_/g, ' ')}`;
  return EVENT_LABELS[type] ?? type;
}

function OwnerAttemptRow({
  a,
  isOwner,
  onCancel,
  canceling,
}: {
  a: PaymentAttemptDTO;
  isOwner: boolean;
  onCancel: (a: PaymentAttemptDTO) => void;
  canceling: boolean;
}) {
  const cancelable = ['created', 'requires_payment_method', 'requires_confirmation', 'requires_action', 'payment_failed'].includes(a.status);
  return (
    <li className="card stack" style={{ listStyle: 'none' }}>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap' }}>
        <strong>{formatCents(a.amountCents)}</strong>
        {a.statusSource === 'client' ? (
          <span className="badge">Unconfirmed (browser report): {attemptStatusLabel(a.status)}</span>
        ) : (
          <AttemptStatusBadge status={a.status} />
        )}
      </div>
      <dl className="kv-grid" style={{ margin: 0 }}>
        <div>
          <dt className="muted">Tenant</dt>
          <dd>{a.tenantName ?? 'Unknown (not linked)'}</dd>
        </div>
        <div>
          <dt className="muted">Started</dt>
          <dd>{formatDateTime(a.startedAt)}</dd>
        </div>
        {a.initiatedAt && (
          <div>
            <dt className="muted">Bank debit submitted</dt>
            <dd>{formatDateTime(a.initiatedAt)}</dd>
          </div>
        )}
        {a.status === 'processing' && a.expectedSettlementDate && (
          <div>
            <dt className="muted">Expected to clear (estimate)</dt>
            <dd>{formatDate(a.expectedSettlementDate)}</dd>
          </div>
        )}
        {a.succeededAt && (
          <div>
            <dt className="muted">Cleared</dt>
            <dd>{formatDateTime(a.succeededAt)}</dd>
          </div>
        )}
        {a.bankLast4 && (
          <div>
            <dt className="muted">Bank</dt>
            <dd>
              {a.bankName ?? 'Bank account'} ending {a.bankLast4}
            </dd>
          </div>
        )}
        {a.failureReason && (
          <div>
            <dt className="muted">Failure</dt>
            <dd>
              {a.failureReason}
              {a.failureCode ? <span className="muted"> ({a.failureCode})</span> : null}
            </dd>
          </div>
        )}
        {a.cancellationReason && (
          <div>
            <dt className="muted">Cancel reason</dt>
            <dd>{a.cancellationReason.replace(/_/g, ' ')}</dd>
          </div>
        )}
      </dl>
      {a.statusSource === 'client' && (
        <p className="muted" style={{ margin: 0 }}>
          Reported by the tenant&rsquo;s browser — waiting for Stripe to confirm.
        </p>
      )}
      {a.status === 'requires_action' && (
        <p className="muted" style={{ margin: 0 }}>
          No money has moved yet — the tenant must finish a step
          {a.nextActionType === 'verify_with_microdeposits' ? ' (verify the small test deposits in their bank)' : ''}.
        </p>
      )}
      {a.ledgerPostMissing && (
        <StatusMessage tone="warn">Cleared at Stripe but not yet posted to the ledger.</StatusMessage>
      )}
      <details>
        <summary>Timeline ({a.events.length})</summary>
        <ol className="stack" style={{ margin: '0.5rem 0 0', paddingLeft: '1.25rem' }}>
          {a.events.map((e) => (
            <li key={e.id}>
              <span>{formatDateTime(e.occurredAt)}</span> — {eventLabel(e.type)}
              {e.statusAfter ? <span className="muted"> → {attemptStatusLabel(e.statusAfter)}</span> : null}
              {e.source === 'stripe' && !e.statusAfter ? <span className="muted"> (no change)</span> : null}
            </li>
          ))}
        </ol>
      </details>
      <div className="row" style={{ flexWrap: 'wrap' }}>
        <a href={a.stripeDashboardUrl} target="_blank" rel="noopener noreferrer">
          Open in Stripe<span className="visually-hidden"> ({a.stripePaymentIntentId})</span>
        </a>
        <code className="muted" style={{ fontSize: '0.8rem' }}>
          {a.stripePaymentIntentId}
        </code>
        {isOwner && cancelable && (
          <button
            type="button"
            className="secondary"
            disabled={canceling}
            onClick={() => {
              if (window.confirm(`Cancel this ${formatCents(a.amountCents)} payment attempt at Stripe? The tenant can start a new one.`)) onCancel(a);
            }}
          >
            Cancel attempt<span className="visually-hidden"> of {formatCents(a.amountCents)}</span>
          </button>
        )}
      </div>
    </li>
  );
}

/** Owner/staff "Payment activity" panel for one lease (every attempt, expandable timeline). */
export function LeasePaymentActivity({ leaseId, isOwner }: { leaseId: string; isOwner: boolean }) {
  const queryClient = useQueryClient();
  const key = ['admin', 'payment-attempts', leaseId];
  const query = useQuery({ queryKey: key, queryFn: () => api.paymentAttempts.list({ leaseId }) });
  const cancel = useMutation({
    mutationFn: (a: PaymentAttemptDTO) => api.paymentAttempts.cancel(a.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: key });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'payment-summary'] });
    },
  });
  if (query.isLoading) return <Loading label="Loading payment activity…" />;
  if (query.isError || !query.data) return <ErrorState message={errorMessage(query.error)} onRetry={() => void query.refetch()} />;
  const attempts = query.data.attempts;
  return (
    <div className="stack">
      {cancel.isError && <StatusMessage tone="err">{errorMessage(cancel.error)}</StatusMessage>}
      {attempts.length === 0 ? (
        <p className="muted">No online payment activity on this lease.</p>
      ) : (
        <ul className="stack" style={{ padding: 0, margin: 0 }}>
          {attempts.map((a) => (
            <OwnerAttemptRow key={a.id} a={a} isOwner={isOwner} onCancel={(x) => cancel.mutate(x)} canceling={cancel.isPending} />
          ))}
        </ul>
      )}
    </div>
  );
}

/** Plain-language sentence for the tenant's own attempt. */
export function tenantAttemptSentence(a: TenantPaymentAttemptDTO): string {
  const amt = formatCents(a.amountCents);
  switch (a.status) {
    case 'processing':
      return `Your ${amt} bank payment is processing — started ${formatDate(a.initiatedAt ?? a.startedAt)}. Banks usually take up to 4 business days${a.expectedSettlementDate ? ` (around ${formatDate(a.expectedSettlementDate)})` : ''}. It is not applied to your balance until it clears.`;
    case 'succeeded':
      return a.postedToLedger
        ? `Your ${amt} bank payment cleared${a.succeededAt ? ` on ${formatDate(a.succeededAt)}` : ''} and is on your ledger.`
        : `Your ${amt} bank payment cleared — posting to your account. It is shown as pending until it appears on your ledger.`;
    case 'requires_action':
      return a.nextActionType === 'verify_with_microdeposits'
        ? `Your ${amt} bank payment is waiting for you to verify your bank account${a.verificationArrivalAt ? ` (small test deposits arrive by ${formatDate(a.verificationArrivalAt)})` : ''}. No money has moved yet.`
        : `Your ${amt} bank payment needs one more step from you. No money has moved yet.`;
    case 'payment_failed':
      return `Your ${amt} bank payment didn't go through. ${a.failureReason ?? ''} Nothing was charged to your balance — you can try again or pay another way.`;
    case 'canceled':
      return `The ${amt} payment you started on ${formatDate(a.startedAt)} was canceled. No money moved.`;
    case 'reversed':
      return `Your ${amt} bank payment was returned or refunded after it cleared; the amount is owed again.`;
    default:
      return `You started a ${amt} payment on ${formatDate(a.startedAt)} but didn't finish — continue or cancel.`;
  }
}

/** The tenant's own recent payment activity (portal). */
export function TenantPaymentActivityList({
  attempts,
  onCancel,
  busy,
  limit = 5,
}: {
  attempts: TenantPaymentAttemptDTO[];
  onCancel?: (a: TenantPaymentAttemptDTO) => void;
  busy?: boolean;
  limit?: number;
}) {
  if (attempts.length === 0) return <p className="muted">No online payments yet.</p>;
  return (
    <ul className="stack" style={{ padding: 0, margin: 0 }}>
      {attempts.slice(0, limit).map((a) => (
        <li key={a.id} className="card stack" style={{ listStyle: 'none' }}>
          <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap' }}>
            <strong>{formatCents(a.amountCents)}</strong>
            <AttemptStatusBadge status={a.status} label={tenantBadgeLabel(a)} />
          </div>
          <p style={{ margin: 0 }}>{tenantAttemptSentence(a)}</p>
          {a.bankLast4 && (
            <p className="muted" style={{ margin: 0 }}>
              From {a.bankName ?? 'your bank account'} ending {a.bankLast4}
            </p>
          )}
          {a.awaitingConfirmation && (
            <p className="muted" style={{ margin: 0 }}>
              Waiting for confirmation from the payment processor.
            </p>
          )}
          <div className="row" style={{ flexWrap: 'wrap' }}>
            {a.status === 'requires_action' && a.verificationUrl && (
              <a href={a.verificationUrl} target="_blank" rel="noopener noreferrer">
                Verify your bank account
              </a>
            )}
            {a.canContinue && (
              <Link to={`/portal/pay?attempt=${encodeURIComponent(a.id)}`}>
                {a.status === 'payment_failed' ? 'Try again' : 'Continue this payment'}
                <span className="visually-hidden"> of {formatCents(a.amountCents)}</span>
              </Link>
            )}
            {a.canCancel && onCancel && (
              <button
                type="button"
                className="secondary"
                disabled={busy}
                onClick={() => {
                  if (window.confirm(`Cancel the ${formatCents(a.amountCents)} payment you started?`)) onCancel(a);
                }}
              >
                Cancel<span className="visually-hidden"> the {formatCents(a.amountCents)} payment</span>
              </button>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}
